import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX } from 'lucide-react';
import type { ReaderThemeMode } from '../types';

interface AudioPlayerProps {
  audioUrl: string;
  chapterTitle: string;
  theme: ReaderThemeMode;
}

export default function AudioPlayer({ audioUrl, chapterTitle, theme }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isDark = theme === 'dark';
  const bgColor = isDark ? 'rgba(255,255,255,0.06)' : '#f8f8f8';
  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e5e5e5';
  const textColor = isDark ? '#e4e4e7' : '#374151';
  const secondaryColor = isDark ? '#a1a1aa' : '#6b7280';

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onLoadedMetadata = () => {
      setDuration(el.duration);
      setIsLoading(false);
    };
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onEnded = () => setIsPlaying(false);
    const onError = () => {
      setError('Failed to load audio');
      setIsLoading(false);
    };
    const onCanPlay = () => setIsLoading(false);

    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);
    el.addEventListener('canplay', onCanPlay);

    return () => {
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
      el.removeEventListener('canplay', onCanPlay);
    };
  }, [audioUrl]);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
    } else {
      el.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const seek = useCallback((seconds: number) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min(el.currentTime + seconds, duration));
  }, [duration]);

  const toggleMute = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const cycleSpeed = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    const speeds = [1, 1.25, 1.5, 1.75, 2];
    const next = speeds[(speeds.indexOf(playbackRate) + 1) % speeds.length];
    el.playbackRate = next;
    setPlaybackRate(next);
  }, [playbackRate]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    el.currentTime = ratio * duration;
  }, [duration]);

  const formatTime = (s: number): string => {
    if (!isFinite(s) || isNaN(s)) return '0:00';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div style={{
        padding: '0.75rem 1rem',
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '0.75rem',
        color: secondaryColor,
        fontSize: '0.8125rem',
        textAlign: 'center',
      }}>
        Audio unavailable
      </div>
    );
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      padding: '1rem 1.25rem',
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
    }}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Label */}
      <div style={{
        fontSize: '0.75rem',
        color: secondaryColor,
        marginBottom: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontWeight: 600,
      }}>
        Listen to this chapter
      </div>

      {/* Progress bar */}
      <div
        onClick={handleProgressClick}
        style={{
          height: '4px',
          background: isDark ? 'rgba(255,255,255,0.15)' : '#e0e0e0',
          borderRadius: '2px',
          cursor: 'pointer',
          marginBottom: '0.75rem',
          position: 'relative',
        }}
      >
        <div style={{
          height: '100%',
          width: `${progressPercent}%`,
          background: 'var(--accent, #dc2626)',
          borderRadius: '2px',
          transition: 'width 0.1s linear',
        }} />
      </div>

      {/* Controls row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        {/* Left: time */}
        <span style={{ fontSize: '0.75rem', color: secondaryColor, minWidth: '80px' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        {/* Center: playback controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => seek(-15)}
            title="Back 15s"
            style={controlBtnStyle(textColor)}
          >
            <RotateCcw size={16} />
          </button>

          <button
            onClick={togglePlay}
            disabled={isLoading}
            title={isPlaying ? 'Pause' : 'Play'}
            style={{
              ...controlBtnStyle(textColor),
              width: '40px',
              height: '40px',
              background: 'var(--accent, #dc2626)',
              color: '#fff',
              borderRadius: '50%',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isLoading ? (
              <div style={{
                width: '16px', height: '16px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
            ) : isPlaying ? (
              <Pause size={18} />
            ) : (
              <Play size={18} style={{ marginLeft: '2px' }} />
            )}
          </button>

          <button
            onClick={() => seek(30)}
            title="Forward 30s"
            style={controlBtnStyle(textColor)}
          >
            <RotateCw size={16} />
          </button>
        </div>

        {/* Right: speed + mute */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: '80px', justifyContent: 'flex-end' }}>
          <button
            onClick={cycleSpeed}
            title="Playback speed"
            style={{
              ...controlBtnStyle(secondaryColor),
              fontSize: '0.6875rem',
              fontWeight: 700,
              padding: '0.25rem 0.5rem',
            }}
          >
            {playbackRate}x
          </button>
          <button
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
            style={controlBtnStyle(secondaryColor)}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function controlBtnStyle(color: string): React.CSSProperties {
  return {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.375rem',
    borderRadius: '6px',
    transition: 'opacity 0.15s',
  };
}
